import { Interpolation, Theme } from '@emotion/react'
import { FC } from 'react'
import ReactDOM from 'react-dom'
import { MarkerActionCall, useMarkers } from '../../../contexts/annotations'
import { usePlayerState } from '../../../contexts/playerState'
import Marker from './Marker'

const groupCloseMarkers =
  (timelineWidth: number, videoDuration: number) =>
  (
    previousValue: { [key: string]: MarkerActionCall[] },
    currentValue: MarkerActionCall,
  ) => {
    const position = (currentValue.offset / (videoDuration * 1000) || 0) * 100 // offset is in miliseconds
    const markerPixelMargin = 44
    const currentPositionPx = (position * timelineWidth) / 100
    const groupPosition =
      Math.round(currentPositionPx / markerPixelMargin) * markerPixelMargin

    if (previousValue[groupPosition]) {
      previousValue[groupPosition].push(currentValue)
    } else {
      previousValue[groupPosition] = [currentValue]
    }

    return previousValue
  }

const mergeGroupedMarkers = (
  accumulator: MarkerActionCall,
  currentValue: MarkerActionCall,
  _index: number,
  groupMarkersList: MarkerActionCall[],
): MarkerActionCall => {
  accumulator.id = currentValue.id
  accumulator.data = accumulator.data || {}
  accumulator.data.label =
    accumulator?.data?.label ||
    groupMarkersList.reduce(
      (accum: string, { data }) => `${accum}${data.label}<br>`,
      '',
    )
  accumulator.data.color =
    accumulator.data.color === currentValue.data.color ||
    !accumulator.data.color
      ? currentValue.data.color
      : ''
  accumulator.offset =
    accumulator.offset && accumulator.offset < currentValue.offset
      ? accumulator.offset
      : currentValue.offset
  accumulator.data.seek_offset =
    accumulator.offset && accumulator.offset < currentValue.offset
      ? accumulator.data.seek_offset
      : currentValue.data.seek_offset

  return accumulator
}

const Markers: FC = () => {
  const { isInitialized, playerInstance, totalTime } = usePlayerState()
  const markers = useMarkers()

  if (!isInitialized || !playerInstance) return null

  const playerProgressContainer = playerInstance
    .el()
    .querySelector('.vjs-progress-control')
  const timelineWidth = playerInstance.el().clientWidth - 40

  const videoDuration = totalTime

  if (!videoDuration) return null

  const groupedMarkers = markers.reduce(
    groupCloseMarkers(timelineWidth, videoDuration),
    {},
  )
  const newMarkers = Object.keys(groupedMarkers).map((group: string) =>
    groupedMarkers[group].reduce(mergeGroupedMarkers, {} as MarkerActionCall),
  )

  return (
    playerProgressContainer &&
    ReactDOM.createPortal(
      <div css={containerStyles} className="timeline-markers-wrapper">
        {newMarkers.map((item) => {
          const position = (+item.offset / (videoDuration * 1000) || 0) * 100 // offset is in miliseconds
          const seekTime =
            (+item.offset + (item.data.seek_offset || 0)) / 1000 || 0 // time is in seconds

          return position ? (
            <Marker
              key={item.id}
              label={item.data.label}
              position={position}
              time={item.offset}
              color={item.data.color}
              left={position < 10}
              right={position > 90}
              onClick={() => {
                playerInstance.currentTime(seekTime - 1)
              }}
            />
          ) : null
        })}
      </div>,
      playerProgressContainer,
    )
  )
}

export default Markers

const containerStyles: Interpolation<Theme> = {
  height: '8px',
  position: 'absolute',
  left: 0,
  margin: '0 10px',
  top: '50%',
  width: 'calc(100% - 20px)',
  transform: 'translate(0, -50%)',
  zIndex: 1,
}
